import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import {
  clearListings,
  fetchBatchStatus,
  fetchBatches,
  fetchLogs,
  fetchVendorNotifications,
  fetchVendorStatus,
  runImport,
  stopImport,
  uploadCsv,
} from '../services/adminApi'
import { PageShell, StatCard, TerminalLogs } from '../components/shared'
import { useAdminJob } from '../AdminConsole'

type BatchRow = {
  batch_uuid: string
  original_filename: string
  row_count: number
  status: string
  source_type?: string
  job_state?: string
  input_id?: string
  created_at?: string
}

function prettyCity(raw?: string) {
  const value = (raw || '').trim()
  if (!value) return ''
  if (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(value)) return ''
  return value
    .replace(/\.(csv|json)$/i, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function cityFromBatch(b: BatchRow) {
  return prettyCity(b.input_id) || prettyCity((b.original_filename || '').replace(/\.[^.]+$/, ''))
}

function formatBatchDate(iso?: string) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function batchLabel(b: BatchRow) {
  const city = cityFromBatch(b)
  const when = formatBatchDate(b.created_at)
  const file = b.original_filename || b.batch_uuid.slice(0, 8)
  const prefix = b.source_type === 'bnb_enrichment' ? 'AI · ' : ''
  return [prefix + (city || 'Unknown city'), when, `${file} · ${b.row_count} rows`].filter(Boolean).join(' · ')
}

type LogRow = { id: number; level: string; message: string; created_at: string }

type BatchDetail = BatchRow & {
  remaining?: number
  row_status?: Record<string, number>
  job_thread_alive?: boolean
  processed_count?: number
  failed_count?: number
}

export default function ListingImportPage() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { setJobRunning } = useAdminJob()
  const [batches, setBatches] = useState<BatchRow[]>([])
  const [batchUuid, setBatchUuid] = useState('')
  const [batchDetail, setBatchDetail] = useState<BatchDetail | null>(null)
  const [logs, setLogs] = useState<LogRow[]>([])
  const [vendors, setVendors] = useState<Record<string, unknown>[]>([])
  const [chunkSize, setChunkSize] = useState(5)
  const [clearBefore, setClearBefore] = useState(false)
  const [retryFailed, setRetryFailed] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [flash, setFlash] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  const [vendorLookup, setVendorLookup] = useState('')
  const [vendorResult, setVendorResult] = useState<Record<string, unknown> | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const logEndRef = useRef<HTMLDivElement>(null)

  const refreshBatches = useCallback(async () => {
    const json = await fetchBatches()
    const list = (json.batches || []) as BatchRow[]
    setBatches(list)
    if (!batchUuid && list.length > 0) setBatchUuid(list[0].batch_uuid)
  }, [batchUuid])

  const refreshStatus = useCallback(async () => {
    if (!batchUuid) return
    const [statusJson, logsJson] = await Promise.all([fetchBatchStatus(batchUuid), fetchLogs(batchUuid)])
    setBatchDetail(statusJson.batch as BatchDetail)
    setLogs((logsJson.logs || []) as LogRow[])
  }, [batchUuid])

  useEffect(() => {
    refreshBatches().catch(() => {})
  }, [refreshBatches])

  useEffect(() => {
    if (!batchUuid) return
    refreshStatus().catch((e) => setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Refresh failed' }))
    const id = window.setInterval(() => refreshStatus().catch(() => {}), 4000)
    return () => window.clearInterval(id)
  }, [batchUuid, refreshStatus])

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  const rowStatus = batchDetail?.row_status || {}
  const total = batchDetail?.row_count ?? 0
  const done = rowStatus.done ?? batchDetail?.processed_count ?? 0
  const failed = rowStatus.failed ?? batchDetail?.failed_count ?? 0
  const pending = batchDetail?.remaining ?? rowStatus.pending ?? 0
  const progress = total > 0 ? Math.round(((done + failed) / total) * 100) : 0
  const jobState = batchDetail?.job_state || 'idle'
  const isRunning = jobState === 'running' || jobState === 'stopping' || batchDetail?.job_thread_alive

  useEffect(() => {
    setJobRunning(Boolean(isRunning))
    return () => setJobRunning(false)
  }, [isRunning, setJobRunning])

  const handleUpload = async (file: File) => {
    setUploading(true)
    setFlash(null)
    try {
      const json = await uploadCsv(file)
      setBatchUuid(json.batch_uuid)
      setFlash({ type: 'success', text: `Uploaded ${json.row_count} rows` })
      await refreshBatches()
      await refreshStatus()
    } catch (e) {
      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Upload failed' })
    } finally {
      setUploading(false)
    }
  }

  const handleStart = async () => {
    if (!batchUuid) return
    setBusy('start')
    try {
      await runImport(batchUuid, { chunk_size: chunkSize, clear_before: clearBefore, retry_failed_first: retryFailed })
      setFlash({ type: 'info', text: 'Import running — safe to close tab.' })
      await refreshStatus()
    } catch (e) {
      setFlash({ type: 'error', text: e instanceof Error ? e.message : 'Start failed' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <PageShell title="CSV Listing Import" subtitle="Upload Google Maps CSV or enriched BNB exports · batch processing to protect MySQL">
      {flash && (
        <Alert severity={flash.type} sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      {batchDetail && (
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Total" value={total} /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Done" value={done} accent="green" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Pending" value={pending} accent="amber" /></Grid>
          <Grid size={{ xs: 6, sm: 3 }}><StatCard label="Failed" value={failed} accent={failed ? 'rose' : 'slate'} /></Grid>
        </Grid>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper className="admin-panel" elevation={0} sx={{ mb: 2.5 }}>
            <Box className="admin-panel-header">
              <Typography variant="h6" fontWeight={700}>Import batch</Typography>
              <Typography variant="body2" color="text.secondary">Enriched BNB batches appear with an AI tag</Typography>
            </Box>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = '' }} />
              <Box className="admin-upload-zone" onClick={() => !uploading && fileRef.current?.click()} role="button" tabIndex={0}>
                {uploading ? <CircularProgress size={32} sx={{ color: '#C9A227' }} /> : <CloudUploadRoundedIcon sx={{ fontSize: 40, color: '#C9A227', mb: 1 }} />}
                <Typography fontWeight={600}>{uploading ? 'Uploading…' : 'Upload CSV'}</Typography>
                <Typography variant="caption" color="text.secondary">Same columns as Google Maps scrape export</Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Batch"
                  value={batchUuid}
                  onChange={(e) => setBatchUuid(e.target.value)}
                  SelectProps={{
                    renderValue: (value) => {
                      const b = batches.find((x) => x.batch_uuid === value)
                      return b ? batchLabel(b) : 'Select…'
                    },
                  }}
                >
                  <MenuItem value="">Select…</MenuItem>
                  {batches.map((b) => (
                    <MenuItem key={b.batch_uuid} value={b.batch_uuid} sx={{ whiteSpace: 'normal' }}>
                      {batchLabel(b)}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton onClick={() => refreshBatches()} sx={{ border: '1px solid rgba(20,24,32,0.1)', borderRadius: 2 }}>
                  <RefreshRoundedIcon />
                </IconButton>
              </Stack>

              {batchDetail && (
                <Box sx={{ mt: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Chip label={jobState} size="small" sx={{ fontWeight: 600 }} />
                    <Typography variant="caption" fontWeight={600}>{progress}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 999 }} />
                </Box>
              )}

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <TextField type="number" fullWidth size="small" label="Chunk size" value={chunkSize}
                    onChange={(e) => setChunkSize(Math.min(20, Math.max(1, Number(e.target.value) || 5)))} />
                </Grid>
                <Grid size={{ xs: 6, sm: 8 }}>
                  <FormControlLabel control={<Switch size="small" checked={clearBefore} onChange={(e) => setClearBefore(e.target.checked)} />} label={<Typography variant="body2">Clear listings first</Typography>} />
                  <FormControlLabel control={<Switch size="small" checked={retryFailed} onChange={(e) => setRetryFailed(e.target.checked)} />} label={<Typography variant="body2">Retry failed</Typography>} />
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
                <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} disabled={!batchUuid || Boolean(busy) || isRunning} onClick={handleStart}>Start</Button>
                <Button variant="outlined" startIcon={<StopRoundedIcon />} disabled={!batchUuid || !isRunning} onClick={async () => { await stopImport(batchUuid); refreshStatus() }}>Stop</Button>
                <Button variant="outlined" startIcon={<ReplayRoundedIcon />} onClick={() => { setRetryFailed(true); handleStart() }}>Retry failed</Button>
                <Button variant="outlined" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={async () => { if (confirm('Clear all listings?')) { await clearListings(); setFlash({ type: 'success', text: 'Cleared' }) } }}>Clear all</Button>
              </Stack>
            </Box>
          </Paper>

          <Paper className="admin-panel" elevation={0}>
            <Box className="admin-panel-header">
              <Typography variant="h6" fontWeight={700}>Live logs</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <TerminalLogs title="aonlastay-import" logs={logs} logEndRef={logEndRef} />
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper className="admin-panel" elevation={0} sx={{ mb: 2.5 }}>
            <Box className="admin-panel-header"><Typography variant="h6" fontWeight={700}>Vendor lookup</Typography></Box>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={1}>
                <TextField fullWidth size="small" placeholder="ID or username" value={vendorLookup} onChange={(e) => setVendorLookup(e.target.value)} />
                <Button variant="contained" onClick={async () => {
                  const q = vendorLookup.trim()
                  if (!q) return
                  const isId = /^\d+$/.test(q)
                  setVendorResult(await fetchVendorStatus(isId ? Number(q) : undefined, isId ? undefined : q))
                }}><SearchRoundedIcon /></Button>
              </Stack>
              {vendorResult && <pre style={{ marginTop: 12, fontSize: 11, overflow: 'auto' }}>{JSON.stringify(vendorResult, null, 2)}</pre>}
            </Box>
          </Paper>
          <Paper className="admin-panel" elevation={0}>
            <Box className="admin-panel-header" sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight={700}>Vendor alerts</Typography>
              <Button size="small" onClick={async () => batchUuid && setVendors((await fetchVendorNotifications(batchUuid)).vendors || [])}>Load</Button>
            </Box>
            <Box sx={{ p: 2, maxHeight: 360, overflow: 'auto' }}>
              {vendors.map((v, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700}>{(v as { vendor_username?: string }).vendor_username}</Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {isMobile && (
        <Box className="admin-mobile-bar">
          <Stack direction="row" spacing={1}>
            <Button fullWidth variant="contained" disabled={!batchUuid || isRunning} onClick={handleStart}>Start</Button>
            <Button fullWidth variant="outlined" disabled={!isRunning} onClick={async () => stopImport(batchUuid)}>Stop</Button>
          </Stack>
        </Box>
      )}
    </PageShell>
  )
}
